import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { ROUTES } from '@/lib/site'
import { Reveal } from '@/components/motion/Reveal'
import { SplitText } from '@/components/motion/SplitText'

/**
 * Shared hero for the interior routes: breadcrumb, H1 and lede, at one
 * consistent scale so no two pages open at a different size.
 */
export function PageHero({
  crumb,
  title,
  lede,
  children,
}: {
  crumb: string
  title: ReactNode
  lede?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <Reveal variant="fade" duration={500}>
        <nav aria-label="Breadcrumb" className="flex justify-center">
          <ol className="flex flex-wrap items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.12em] text-muted-soft">
            <li>
              <Link to={ROUTES.home.path} className="rounded text-primary link-underline">
                Home
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">{crumb}</li>
          </ol>
        </nav>
      </Reveal>

      {typeof title === 'string' ? (
        <SplitText
          as="h1"
          text={title}
          delay={140}
          step={52}
          className="mx-auto mt-7 block max-w-[15ch] text-balance text-[34px] font-extrabold leading-[1.04] tracking-[-0.032em] sm:text-[48px] lg:text-[60px]"
        />
      ) : (
        <Reveal variant="up" delay={90}>
          <h1 className="mx-auto mt-7 max-w-[15ch] text-balance text-[34px] font-extrabold leading-[1.04] tracking-[-0.032em] sm:text-[48px] lg:text-[60px]">
            {title}
          </h1>
        </Reveal>
      )}

      {lede ? (
        <Reveal variant="up" delay={180}>
          <p className="mx-auto mt-6 max-w-[62ch] text-pretty text-[15px] leading-[1.75] text-muted sm:text-[16px]">
            {lede}
          </p>
        </Reveal>
      ) : null}

      {children ? <div className="mt-9 flex flex-wrap justify-center gap-3">{children}</div> : null}
    </div>
  )
}
